import { TraceFileSchema, TraceStatusSchema } from '@terra/shared-schemas-ai';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import { LifecycleStateFilterSchema } from '@terra/shared-dto-types';
import {
  BlockedReasonSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { TraceAgentNameFilterSchema } from './trace-agent.schema';
import {
  TraceGroupByFieldSchema,
  TraceGroupCountSchema,
  TraceSortFieldSchema,
} from './trace-sort.schema';
import { TRACE_PATH_NAME } from './trace.constants';
import {
  TracePreviewBaseSchema,
  TracePreviewSchema,
  TraceWithMessagesAndFilesSchema,
} from './trace.schema';

const TracesByFilterQuerySchema = z.object({
  targetId: z.string(),
  threadId: z.string().optional(),
  traceId: z.string().optional(),
  name: z.string().optional(),
  signalId: z.string().optional(),
  status: z.union([TraceStatusSchema, z.array(TraceStatusSchema)]).optional(),
});

const TraceFileParamsSchema = z.object({
  applicationId: z.string(),
  threadId: z.string(),
  traceId: z.string(),
  fileId: z.string(),
});

const DeleteTraceParamsSchema = z.object({
  applicationId: z.string(),
  threadId: z.string(),
});

const DeleteTraceResponseSchema = z.object({
  success: z.boolean(),
  deletedCount: z.number(),
});

export const TRACE_BULK_DELETE_MAX_THREADS = 500;

const DeleteTracesBulkBodySchema = z.object({
  threadIds: z
    .array(z.string().min(1))
    .min(1)
    .max(TRACE_BULK_DELETE_MAX_THREADS)
    .describe('Thread IDs of the executions to delete'),
});

const DeleteTracesBulkResponseSchema = z.object({
  success: z.boolean(),
  deletedThreadIds: z.array(z.string()),
  failedThreadIds: z.array(z.string()),
});

const TraceByApplicationIdParamsSchema = z.object({
  applicationId: z.string(),
});

const TraceOrderFieldSchema = z.object({
  field: TraceSortFieldSchema,
  order: z.enum(['asc', 'desc']),
});

const TraceByApplicationIdQuerySchema = z.object({
  signalId: z.string().optional(),
  name: z.string().optional(), // Searches signal.description (objective)
  threadId: z.string().optional(),
  agentName: TraceAgentNameFilterSchema.optional().describe(
    'Filter by trace name (agent name like "neo"); pass several to match any',
  ),
  status: z.union([TraceStatusSchema, z.array(TraceStatusSchema)]).optional(),
  signalStatus: SignalStatusSchema.array().optional(),
  blockedReason: BlockedReasonSchema.array().optional(),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.array().optional(),
  endpointIds: z
    .array(z.string())
    .optional()
    .describe('Filter by endpoint IDs (via signals)'),
  urlPatterns: z
    .array(z.string())
    .optional()
    .describe('Filter by URL patterns (via signals)'),
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of traces to skip (for pagination)'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of traces to return'),
  orderBy: z
    .array(TraceOrderFieldSchema)
    .optional()
    .describe('Sort order for traces list'),
  startDate: z.coerce
    .date()
    .optional()
    .describe('List traces with createdAt >= startDate'),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter executions by linked signal lifecycle (active, archived, or all)',
  ),
});

const TraceBySignalIdParamsSchema = z.object({
  signalId: z.string(),
});

const TraceBySignalIdQuerySchema = z.object({
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter executions by linked signal lifecycle (active, archived, or all)',
  ),
});

const TracesCountByApplicationIdParamsSchema = z.object({
  applicationId: z.string(),
});

const TracesCountByApplicationIdQuerySchema = z.object({
  agentName: TraceAgentNameFilterSchema.optional().describe(
    'Filter by trace name (e.g. "neo"); pass several to match any',
  ),
  name: z
    .string()
    .optional()
    .describe(
      'Search executions by thread, execution or trace id, or linked signal description',
    ),
  signalStatus: SignalStatusSchema.array().optional(),
  blockedReason: BlockedReasonSchema.array().optional(),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.array().optional(),
  startDate: z.coerce
    .date()
    .optional()
    .describe('Count traces with createdAt >= startDate'),
  endDate: z.coerce
    .date()
    .optional()
    .describe('Count traces with createdAt <= endDate'),
  endpointIds: z
    .array(z.string())
    .optional()
    .describe('Filter by endpoint IDs (via signals)'),
  urlPatterns: z
    .array(z.string())
    .optional()
    .describe('Filter by URL patterns (via signals)'),
  groupByStatus: z.coerce
    .boolean()
    .optional()
    .describe(
      'When true, response includes byStatus counts aggregated in the DB',
    ),
  groupBy: TraceGroupByFieldSchema.optional().describe(
    'When set, response includes per-group counts for the grouped executions view',
  ),
  status: z
    .union([TraceStatusSchema, z.array(TraceStatusSchema)])
    .optional()
    .describe('Count only executions with these effective statuses'),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter executions by linked signal lifecycle (active, archived, or all)',
  ),
});

const TracesCountByStatusSchema = z.object({
  RUNNING: z.int().nonnegative(),
  COMPLETED: z.int().nonnegative(),
  FAILED: z.int().nonnegative(),
  PAUSED: z.int().nonnegative(),
  ABORTED: z.int().nonnegative(),
  BLOCKED: z.int().nonnegative(),
});

const TracesCountByApplicationIdReturnSchema = z.object({
  total: z.int().nonnegative(),
  byStatus: TracesCountByStatusSchema.optional(),
  groups: z.array(TraceGroupCountSchema).optional(),
});

const TraceCountsByEndpointParamsSchema = z.object({
  applicationId: z.string(),
});

const TraceCountsByEndpointQuerySchema = z.object({
  agentName: z
    .string()
    .optional()
    .describe('Filter by trace name (e.g. "neo")'),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter executions by linked signal lifecycle (active, archived, or all)',
  ),
});

const TraceCountsByEndpointReturnSchema = z.object({
  endpointCounts: z.array(
    z.object({
      endpointId: z.string().nullable(),
      traceCount: z.int().nonnegative(),
    }),
  ),
});

export const TraceContract = terraContract
  .prefix(`/${TRACE_PATH_NAME}`)
  .router({
    getTracesByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'Get traces by filter',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: TracesByFilterQuerySchema }))
      .output(z.array(TraceWithMessagesAndFilesSchema)),
    getTracesByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}',
        summary: 'Get traces by application ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TraceByApplicationIdParamsSchema,
          query: TraceByApplicationIdQuerySchema,
        }),
      )
      .output(
        z.object({
          traces: z.array(TracePreviewSchema),
          totalCount: z
            .int()
            .nonnegative()
            .describe('Total number of traces matching the filter'),
        }),
      ),
    getTracesBySignalId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/signal/{signalId}',
        summary: 'Get traces by signal ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TraceBySignalIdParamsSchema,
          query: TraceBySignalIdQuerySchema,
        }),
      )
      .output(z.array(TracePreviewBaseSchema)),
    getTracesCountByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/count',
        summary: 'Get trace counts by application ID (aggregated in DB)',
        description:
          'Returns total count and optionally counts by status. Supports date range filters. Use for dashboard KPIs and execution state distribution without fetching all traces.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TracesCountByApplicationIdParamsSchema,
          query: TracesCountByApplicationIdQuerySchema,
        }),
      )
      .output(TracesCountByApplicationIdReturnSchema),
    getTraceCountsByEndpoint: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/endpoint-counts',
        summary: 'Get neo trace counts grouped by signal endpoint',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TraceCountsByEndpointParamsSchema,
          query: TraceCountsByEndpointQuerySchema,
        }),
      )
      .output(TraceCountsByEndpointReturnSchema),
    getTraceFile: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{threadId}/files/{fileId}',
        summary: 'Get trace file by thread ID and file ID',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TraceFileParamsSchema }))
      .output(TraceFileSchema),
    deleteTrace: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/application/{applicationId}/thread/{threadId}',
        summary: 'Delete a trace and all related data by thread ID',
        description:
          'Deletes all trace data for a specific thread ID, including messages, files, and the trace records themselves.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: DeleteTraceParamsSchema }))
      .output(DeleteTraceResponseSchema),
    deleteTracesBulk: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/threads/bulk-delete',
        summary: 'Delete several executions by thread ID',
        description:
          'Deletes all trace data for each given thread ID, including messages, files, and the trace records themselves. Thread IDs that could not be deleted are returned as failed.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TraceByApplicationIdParamsSchema,
          body: DeleteTracesBulkBodySchema,
        }),
      )
      .output(DeleteTracesBulkResponseSchema),
  });

export type TraceOrderFieldDto = z.infer<typeof TraceOrderFieldSchema>;
export type DeleteTracesBulkResponseDto = z.infer<
  typeof DeleteTracesBulkResponseSchema
>;
